// TimezoneSelect.jsx - Enhanced timezone selector with search
import React, { useState, useEffect } from 'react';
import {
    Autocomplete,
    TextField,
    Box,
    Typography,
    Chip
} from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const TimezoneSelect = ({
    value = '',
    onChange,
    error = false,
    helperText = '',
    required = false,
    disabled = false,
    label = 'Zona Horaria',
    ...props
}) => {
    const { t } = useTranslation();
    const [timezones, setTimezones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTimezone, setSelectedTimezone] = useState(null);

    // Load timezones from JSON
    useEffect(() => {
        const loadTimezones = async () => {
            try {
                const response = await fetch('/countries-master/json/timezones.json');
                const data = await response.json();

                // Transform data to usable format
                const transformed = data.map(tz => ({
                    value: tz.value,
                    label: tz.text,
                    offset: tz.offset,
                    abbr: tz.abbr,
                    isDST: tz.isdst,
                    utcIdentifiers: tz.utc || [tz.value],
                    standardName: tz.value
                }));

                // Sort by offset
                const sorted = transformed.sort((a, b) => {
                    if (a.offset !== b.offset) {
                        return a.offset - b.offset;
                    }
                    return a.label.localeCompare(b.label);
                });

                setTimezones(sorted);
            } catch (error) {
                console.error(t('hardcoded.components_common_timezoneselect.error_loading_timezones'), error);
                // Fallback timezones
                setTimezones([
                    { value: 'America/Mexico_City', label: t('hardcoded.components_common_timezoneselect.utc-0600_ciudad_de_mexico'), offset: -6 },
                    { value: t('hardcoded.components_common_timezoneselect.americanew_york'), label: '(UTC-05:00) Nueva York', offset: -5 },
                    { value: t('hardcoded.components_common_timezoneselect.americachicago'), label: '(UTC-06:00) Chicago', offset: -6 },
                    { value: t('hardcoded.components_common_timezoneselect.americalos_angeles'), label: t('hardcoded.components_common_timezoneselect.utc-0800_los_angeles'), offset: -8 },
                    { value: 'Europe/Madrid', label: '(UTC+01:00) Madrid', offset: 1 },
                    { value: 'UTC', label: '(UTC+00:00) UTC', offset: 0 }
                ]);
            } finally {
                setLoading(false);
            }
        };

        loadTimezones();
    }, []);

    // Set selected timezone when value changes or timezones load
    useEffect(() => {
        if (value && timezones.length > 0) {
            const found = timezones.find(tz =>
                tz.value === value ||
                tz.utcIdentifiers?.includes(value) ||
                tz.standardName === value
            );
            setSelectedTimezone(found || null);
        } else if (!value && timezones.length > 0) {
            // Try to detect user's timezone
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const found = timezones.find(tz =>
                    tz.value === userTimezone ||
                    tz.utcIdentifiers?.includes(userTimezone)
                );
                if (found) {
                    setSelectedTimezone(found);
                    onChange(found.value);
                }
            } catch (e) {
                // If detection fails, use default (Mexico City)
                const mexicoCity = timezones.find(tz => tz.value === 'America/Mexico_City');
                if (mexicoCity) {
                    setSelectedTimezone(mexicoCity);
                    onChange(mexicoCity.value);
                }
            }
        }
    }, [value, timezones, onChange]);

    const handleChange = (event, newValue) => {
        setSelectedTimezone(newValue);
        if (newValue) {
            onChange(newValue.value);
        } else {
            onChange('');
        }
    };

    const getOffsetLabel = (offset) => {
        const hours = Math.floor(Math.abs(offset));
        const minutes = Math.abs(offset % 1) * 60;
        const sign = offset >= 0 ? '+' : '-';

        if (minutes > 0) {
            return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        return `UTC${sign}${hours.toString().padStart(2, '0')}:00`;
    };

    return (
        <Autocomplete
            options={timezones}
            value={selectedTimezone}
            onChange={handleChange}
            loading={loading}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={getOffsetLabel(option.offset)}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                            <Typography variant="body2">
                                {option.label}
                            </Typography>
                        </Box>
                        {option.abbr && (
                            <Typography variant="caption" color="text.secondary">
                                {option.abbr} - {option.value}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    placeholder={t('hardcoded.components_common_timezoneselect.buscar_zona_horaria')}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <ScheduleIcon sx={{ mr: 1, color: 'action.active' }} />
                                {params.InputProps.startAdornment}
                            </>
                        )
                    }}
                    {...props}
                />
            )}
            groupBy={(option) => {
                // Group by region
                const value = option.value;
                if (value.startsWith('America/')) return t('hardcoded.components_common_timezoneselect.america');
                if (value.startsWith('Europe/')) return 'Europa';
                if (value.startsWith('Asia/')) return 'Asia';
                if (value.startsWith('Africa/')) return t('hardcoded.components_common_timezoneselect.africa');
                if (value.startsWith('Australia/')) return 'Australia';
                if (value.startsWith('Pacific/')) return t('hardcoded.components_common_timezoneselect.pacifico');
                return 'Otros';
            }}
        />
    );
};

export default TimezoneSelect;